// ──────────────────────────────────────────────────────────────────────────
// Local-time, midnight-anchored date utilities.
//
// The app's "today" rolls over at the user's local midnight (00:00). A
// check-in or agenda created at 2 AM counts toward the new day; the
// dashboard, agenda, and brief all flip together exactly when the wall
// clock crosses midnight in whatever timezone the device is set to.
//
// (Earlier versions used a 3 AM anchor — kept for the late-night user who
// considered "Tuesday at 2 AM" to still be Monday — but feedback was that
// midnight feels right for a habit tracker, especially when the user is
// reviewing the day at the end of the night.)
//
// Two key formats live alongside each other:
//
//   - Legacy UI key:  "YYYY-M-D"   (month 0-indexed, no padding)
//                     e.g. "2026-3-18" for April 18 2026
//                     Used for the dashboard grid cell keys.
//
//   - Server key:     "YYYY-MM-DD" (month 1-indexed, zero-padded)
//                     Used for the Postgres `date` column and URL params.
//
// Both represent the same anchored local day. Use `toLegacyKey` /
// `toIsoKey` / `parseIsoKey` to convert.
// ──────────────────────────────────────────────────────────────────────────

// The user's "today" in their device-local timezone, anchored at midnight.
// Returns { year, month, day } where month is 0-indexed.
export function anchoredToday() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
}

export function todayLegacyKey() {
  const t = anchoredToday();
  return `${t.year}-${t.month}-${t.day}`;
}

export function todayIsoKey() {
  const t = anchoredToday();
  return `${t.year}-${String(t.month + 1).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
}

// The calendar day immediately before today. Used by the Briefing scene
// to offer pulling unchecked action items forward from the previous day's
// agenda. Must land on the literal previous calendar day, NOT "the most
// recent agenda the user made" which could be several days back. If the
// user skipped a day, we do not offer carryover from two days ago.
export function yesterdayIsoKey() {
  const t = anchoredToday();
  // Construct in local time then walk back one calendar day so DST
  // transitions do not produce a 23- or 25-hour day bug.
  const d = new Date(t.year, t.month, t.day);
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function legacyToIso(legacyKey) {
  // "YYYY-M-D" -> "YYYY-MM-DD"
  const [y, m, d] = legacyKey.split('-').map(Number);
  if ([y, m, d].some(Number.isNaN)) return null;
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function isoToLegacy(isoKey) {
  // "YYYY-MM-DD" -> "YYYY-M-D"  (M 0-indexed)
  const [y, m, d] = isoKey.split('-').map(Number);
  if ([y, m, d].some(Number.isNaN)) return null;
  return `${y}-${m - 1}-${d}`;
}

// Milliseconds until the next local midnight (00:00). Used by long-lived
// scenes to schedule a soft "flip the visible day" timer so a user who
// leaves the app open across midnight sees today's data without needing
// to restart.
export function msUntilNextMidnight() {
  const now = new Date();
  const next = new Date(now);
  next.setDate(next.getDate() + 1);
  next.setHours(0, 0, 0, 0);
  return next - now;
}

// Backwards-compatibility alias. Earlier code imported `msUntilNext3am`;
// we now anchor at midnight, so keep the same name working but route to
// the midnight implementation. New callers should use `msUntilNextMidnight`
// directly.
export function msUntilNext3am() {
  return msUntilNextMidnight();
}

// Niceties used by briefing.
export function niceDateDisplay() {
  const t = anchoredToday();
  const d = new Date(t.year, t.month, t.day);
  const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const mons = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${dows[d.getDay()]} · ${mons[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function longDateGreeting() {
  const t = anchoredToday();
  const d = new Date(t.year, t.month, t.day);
  const dows = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const mons = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const day = d.getDate();
  const suffix = (day % 10 === 1 && day !== 11) ? 'st'
    : (day % 10 === 2 && day !== 12) ? 'nd'
    : (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
  return { dow: dows[d.getDay()], month: mons[d.getMonth()], day, suffix };
}
